import { useCallback, useEffect, useState } from "react";
import usePersistedOpen from "../../hooks/usePersistedOpen";
import { Link } from "react-router-dom";
import { Sparkles, Check, X, Loader2, ChevronDown, ExternalLink, Shuffle, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { notifyMergeUnits } from "../../lib/mergeNotice";
import { Button } from "../ui/button";

function Side({ p, testId, onArchive }) {
  const pid = p.supplier_product_id || p.id;
  return (
    <div className="relative min-w-0 flex-1">
      <Link to={`/products/${pid}`} className="group block rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 pr-9 hover:border-emerald-400 hover:bg-white transition-colors" title="Open product" data-testid={testId}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">{p.supplier_name}</p>
        <p className="text-sm font-medium text-slate-900 leading-snug group-hover:underline">{p.display_name || p.description} <ExternalLink className="inline h-3 w-3 text-slate-400 group-hover:text-emerald-600 -mt-0.5" /></p>
        {p.display_name && p.display_name !== p.description && <p className="text-[11px] text-violet-700 truncate" title="Merged under this name at the supplier">on invoice: {p.description}</p>}
        <p className="text-xs text-slate-500 font-mono mt-0.5">
          {p.pack_size ? `${p.pack_size} · ` : ""}{gbp(p.price)}{p.unit_price != null && p.unit_label !== "per pack" ? ` · ${gbp(p.unit_price)} ${p.unit_label}` : ""}
        </p>
      </Link>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onArchive(pid, p.display_name || p.description); }} className="absolute top-1.5 right-1.5 p-1 text-slate-300 hover:text-amber-700 rounded hover:bg-amber-50" title="Archive — keep history, hide from matching, buying and this list" aria-label="Archive product" data-testid={`${testId}-archive`}>
        <Archive className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AiMatchPanel({ onChanged }) {
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = usePersistedOpen("ai-matching", true);
  const { restaurantId } = useAuth();

  const load = useCallback(() => {
    Promise.all([api.get("/product-matches", { params: { status: "suggested", restaurant_id: restaurantId || undefined } }), api.get("/product-matches/summary")])
      .then(([a, b]) => { setItems(a.data); setSummary(b.data); setRunning(b.data.running); })
      .catch((e) => toast.error(errorMessage(e)));
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => api.get("/product-matches/summary").then((r) => {
      if (!r.data.running) { setRunning(false); toast.success(`AI matching finished · ${r.data.suggested} suggestion${r.data.suggested === 1 ? "" : "s"} waiting for you`); load(); onChanged?.(); }
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [running, load, onChanged]);

  const run = async () => {
    try {
      const { data } = await api.post("/product-matches/run");
      if (!data.started) { toast.success("Nothing new to check — all cross-supplier pairs have been reviewed."); return; }
      toast.info(`Checking ${data.pairs_to_check} product pair${data.pairs_to_check === 1 ? "" : "s"} with AI…`);
      setRunning(true);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const decide = async (m, action) => {
    setItems((list) => list.filter((x) => x.id !== m.id));
    try {
      const same = m.product_a.supplier_id === m.product_b.supplier_id;
      const preferred = [m.product_a.description, m.product_b.description].sort((x, y) => x.length - y.length)[0];
      const { data } = await api.post(`/product-matches/${m.id}/${action}`, action === "confirm" ? { preferred_name: same ? preferred : null } : undefined);
      const bucket = action === "confirm" ? "confirmed" : action === "alternative" ? "alternative" : "rejected";
      toast.success(action === "confirm" ? (same ? `Merged into one product as "${preferred}" — change the name any time from the Products list.` : "Linked as equivalents — each supplier keeps its own product; compared when you order.")
        : action === "alternative" ? "Saved as alternatives — both stay separate products, but they are compared per kg / litre / item on the graph and when you order." : "Kept separate.");
      notifyMergeUnits(data?.units);
      setSummary((s) => (s ? { ...s, suggested: Math.max(0, s.suggested - 1), [bucket]: (s[bucket] || 0) + 1 } : s));
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); load(); }
  };

  const archiveProduct = async (productId, name) => {
    setItems((list) => list.filter((x) => (x.product_a.supplier_product_id || x.product_a.id) !== productId && (x.product_b.supplier_product_id || x.product_b.id) !== productId));
    try {
      await api.post("/supplier-products/archive", { product_ids: [productId], archived: true });
      toast.success(`"${name}" archived — moved to that supplier's archive. History kept.`);
      setSummary((s) => s ? { ...s, suggested: Math.max(0, (s.suggested || 0)) } : s);
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); load(); }
  };

  const count = items?.length || 0;
  return (
    <section className="bg-white border border-slate-200 rounded-xl mb-6 overflow-hidden fade-up" data-testid="ai-match-panel">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <span className="h-9 w-9 rounded-lg bg-violet-50 text-violet-700 grid place-items-center shrink-0"><Sparkles className="h-4.5 w-4.5" size={18} /></span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">AI product matching
            {count > 0 && <span className="text-[11px] font-bold bg-violet-600 text-white rounded-full px-2 py-0.5" data-testid="ai-match-count">{count}</span>}
          </h2>
          <p className="text-xs text-slate-500 truncate">GPT spots the same item written differently — across suppliers or by one supplier (e.g. CHK FILET vs CHICKEN FILLET). Say "Same product" once and it is remembered for every future invoice.{summary ? ` · ${summary.confirmed} confirmed · ${summary.alternative || 0} alternatives · ${summary.rejected} kept separate` : ""}</p>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={running} data-testid="run-ai-matching-btn" className="shrink-0">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}<span className="hidden sm:inline ml-1.5">{running ? "Checking…" : "Run AI matching"}</span>
        </Button>
        <button onClick={() => setOpen((v) => !v)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid="ai-match-toggle" aria-label="Toggle"><ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {open && (
        items === null ? <p className="px-4 py-4 text-sm text-slate-500">Loading…</p> : count === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500" data-testid="ai-match-empty">No pending suggestions. New invoices are checked automatically after you confirm them.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="ai-match-list">
            {items.map((m) => (
              <li key={m.id} className="px-4 py-3" data-testid={`ai-match-${m.id}`}>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
                  <Side p={m.product_a} testId={`ai-match-${m.id}-a`} onArchive={archiveProduct} />
                  <span className="self-center text-xs font-semibold text-slate-400">=</span>
                  <Side p={m.product_b} testId={`ai-match-${m.id}-b`} onArchive={archiveProduct} />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                  <p className="text-xs text-slate-500 min-w-0"><span className="font-semibold text-violet-700">AI · {Math.round((m.ai_confidence || 0) * 100)}%</span>{m.ai_reason ? ` — ${m.ai_reason}` : ""}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => decide(m, "reject")} data-testid={`ai-match-${m.id}-reject`}><X className="h-4 w-4 mr-1" /> Not the same</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(m, "alternative")} className="border-amber-300 text-amber-800 hover:bg-amber-50" title="Different products that can replace each other — kept separate, compared together" data-testid={`ai-match-${m.id}-alternative`}><Shuffle className="h-4 w-4 mr-1" /> Alternative product</Button>
                    <Button size="sm" onClick={() => decide(m, "confirm")} className={`${m.product_a.supplier_id === m.product_b.supplier_id ? "bg-emerald-600 hover:bg-emerald-700" : "bg-sky-600 hover:bg-sky-700"} text-white`} data-testid={`ai-match-${m.id}-confirm`}><Check className="h-4 w-4 mr-1" /> {m.product_a.supplier_id === m.product_b.supplier_id ? "Same product — merge" : "Link as equivalent"}</Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  );
}
